import React from 'react';
import { Link } from 'react-router-dom';
import { 
  FiClipboard, FiAlertTriangle, FiShoppingCart, FiClock, FiCpu
} from 'react-icons/fi';

function StandardDashboard({ user, dashboardData }) {
  // Use the awaiting_my_approvals data from backend instead of filtering all_requisitions
  // This ensures we see ALL requisitions awaiting our approval, not just those from our department
  const currentUserId = String(user?.user_id || '');
  const pendingApprovals = dashboardData.awaiting_my_approvals || [];
  
  // Helper function to check if current user is the approver at this stage (including delegates)
  const isUserCurrentApprover = (req) => {
    if (!user || !user.user_id) return false;
    
    const userId = String(user.user_id);
    const reqStatus = req.requisition_status;
    
    // Check if it's the user's turn based on status
    if (reqStatus === 0) {
      // First approval stage
      return String(req.mid) === userId || String(req.mid_delegate?.user_id) === userId;
    } else if (reqStatus === 1) {
      // Second approval stage
      return String(req.second) === userId || String(req.second_delegate?.user_id) === userId;
    } else if (reqStatus === 2 && req.specification_approvals >= 3) {
      // Third approval stage (only if required)
      // Check if quote exceeds site limit to determine approver type
      const quotationTotal = parseFloat(req.quotation_total?.toString().replace(/[^\d.-]/g, '') || 0);
      const siteLimit = 20000; // Default site limit
      
      if (quotationTotal > siteLimit) {
        // High value: SAP approver
        return String(req.approval) === userId || String(req.sap_delegate?.user_id) === userId;
      } else {
        // Standard value: PM role (role 4)
        return user.role === 4 || user.role === '4';
      }
    }
    
    return false;
  };

  // Helper function to check if approver is waiting for more information from requester
  const isApproverWaitingForInfo = (req) => {
    if (!user || !user.user_id) return false;
    
    // Must be the current approver for this to matter
    if (!isUserCurrentApprover(req)) return false;
    
    // Check if there's a last comment by this approver
    const userId = String(user.user_id);
    const lastCommentUser = req.last_comment_user ? String(req.last_comment_user) : null;
    const lastCommentApproved = req.last_comment_approved;
    const lastCommentText = req.last_comment_text || '';
    
    // Return true if:
    // 1. Last comment was made by current user (approver)
    // 2. Last comment was NOT an approval (comment_approved = 0)
    // 3. Last comment was NOT a rejection (doesn't contain rejection keywords)
    if (lastCommentUser === userId && 
        lastCommentApproved === 0 && 
        !lastCommentText.toLowerCase().includes('rejection reason:') &&
        !lastCommentText.toLowerCase().includes('rejected')) {
      return true;
    }
    
    return false;
  };
  
  const isApprover = pendingApprovals.length > 0 || 
    (dashboardData.all_requisitions || []).some(req => 
      String(req.mid) === currentUserId || 
      String(req.second) === currentUserId || 
      String(req.approval) === currentUserId ||
      (req.mid_delegate && String(req.mid_delegate) === currentUserId) ||
      (req.second_delegate && String(req.second_delegate) === currentUserId) ||
      (req.sap_delegate && String(req.sap_delegate) === currentUserId)
    );

  return (
    <div className="space-y-4">
      {/* Header */}
      <div style={{background: 'var(--gradient-primary)'}} className="rounded-xl p-4 text-white shadow-lg">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold mb-1 text-white">Dashboard</h1>
            <p className="text-white opacity-90 text-sm">
              {isApprover ? 'Approver dashboard - manage requisitions and approvals' : 'Overview of your department activities and requests'}
            </p>
          </div>
          <div className="hidden md:block">
            <div className="bg-white bg-opacity-10 rounded-lg p-3 backdrop-blur-sm">
              <div className="text-white text-xs opacity-75">Welcome back,</div>
              <div className="text-white font-semibold">{user?.user_full_name}</div>
              {isApprover && (
                <div className="text-white text-xs opacity-75 mt-1">Approver</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      {/* <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        {isApprover && (
          <div className="bg-white rounded-xl p-3 shadow-md border-l-4 hover:shadow-lg transition-all flex items-center gap-3" style={{borderLeftColor: '#ffc107'}}>
            <div className="shrink-0 w-10 h-10 rounded-lg flex items-center justify-center" style={{backgroundColor: '#fff3cd'}}>
              <FiClock className="w-5 h-5" style={{color: '#ffc107'}} />
            </div>
            <div className="flex flex-col leading-tight">
              <span className="text-2xl font-bold" style={{color: 'var(--color-warning)'}}>{pendingApprovals.length}</span>
              <span className="text-[10px] font-semibold tracking-wide text-gray-600 uppercase">Pending Approvals</span>
            </div>
          </div>
        )}
        <div className="bg-white rounded-xl p-3 shadow-md border-l-4 hover:shadow-lg transition-all flex items-center gap-3" style={{borderLeftColor: '#0086D4'}}>
          <div className="shrink-0 w-10 h-10 rounded-lg flex items-center justify-center" style={{backgroundColor: '#e3f2fd'}}>
            <FiClipboard className="w-5 h-5" style={{color: '#0086D4'}} />
          </div>
          <div className="flex flex-col leading-tight">
            <span className="text-2xl font-bold" style={{color: 'var(--color-primary)'}}>{dashboardData.open_department_rfq || 0}</span>
            <span className="text-[10px] font-semibold tracking-wide text-gray-600 uppercase">Published RFQs</span>
          </div>
        </div>
        <div className="bg-white rounded-xl p-3 shadow-md border-l-4 hover:shadow-lg transition-all flex items-center gap-3" style={{borderLeftColor: '#ffc107'}}>
          <div className="shrink-0 w-10 h-10 rounded-lg flex items-center justify-center" style={{backgroundColor: '#fff3cd'}}>
            <FiAlertTriangle className="w-5 h-5" style={{color: '#ffc107'}} />
          </div>
          <div className="flex flex-col leading-tight">
            <span className="text-2xl font-bold" style={{color: 'var(--color-warning)'}}>{dashboardData.pending_requisitions || 0}</span>
            <span className="text-[10px] font-semibold tracking-wide text-gray-600 uppercase">Pending Requisitions</span>
          </div>
        </div>
        <div className="bg-white rounded-xl p-3 shadow-md border-l-4 hover:shadow-lg transition-all flex items-center gap-3" style={{borderLeftColor: '#28a745'}}>
          <div className="shrink-0 w-10 h-10 rounded-lg flex items-center justify-center" style={{backgroundColor: '#d4edda'}}>
            <FiShoppingCart className="w-5 h-5" style={{color: '#28a745'}} />
          </div>
          <div className="flex flex-col leading-tight">
            <span className="text-2xl font-bold" style={{color: 'var(--color-success)'}}>0</span>
            <span className="text-[10px] font-semibold tracking-wide text-gray-600 uppercase">Pending PO</span>
          </div>
        </div>
      </div> */}

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Left Column - Requisitions (Approvals or My Requisitions) */}
        {isApprover && pendingApprovals.length > 0 ? (
          <div className="bg-white rounded-xl shadow-lg border" style={{borderColor: 'var(--border-light)'}}>
            <div className="px-2 py-1 border-b bg-gradient-to-r from-amber-50 to-white" style={{borderColor: 'var(--border-light)'}}>
              <div className="flex items-baseline justify-between pt-1 pb-0">
                <div className="flex items-baseline gap-2">
                  <div className="w-6 h-6 rounded-md flex items-center justify-center mt-0.5" style={{backgroundColor: '#fff3cd'}}>
                    <FiClipboard className="w-3.5 h-3.5" style={{color: '#ffc107'}} />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold leading-tight" style={{color: 'var(--text-primary)'}}>
                      Awaiting Your Approval
                    </h3>
                    <p className="text-[10px] text-gray-500 leading-tight">{pendingApprovals.length} pending</p>
                  </div>
                </div>
                <Link
                  to="/dashboard/requisitions?status=awaiting_my_approvals"
                  className="text-[11px] font-semibold hover:underline px-2 py-0.5 rounded-md hover:bg-gray-100 transition-colors"
                  style={{color: 'var(--color-primary)'}}>
                  View All →
                </Link>
              </div>
            </div>
            <div className="divide-y" style={{borderColor: 'var(--border-light)'}}>
              <div className="max-h-[600px] overflow-y-auto">
                <table className="min-w-full text-xs">
                  <thead>
                    <tr className="bg-gray-50 text-gray-700">
                      <th className="font-semibold px-3 py-2 text-left">Number</th>
                      <th className="font-semibold px-3 py-2 text-left">Project</th>
                      <th className="font-semibold px-3 py-2 text-left">Requester</th>
                      <th className="font-semibold px-3 py-2 text-left">Stage</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingApprovals.map((req, index) => {
                      let approvalStage = 'First';
                      if (String(req.second) === currentUserId || (req.second_delegate && String(req.second_delegate) === currentUserId)) {
                        approvalStage = 'Second';
                      } else if (String(req.approval) === currentUserId || (req.sap_delegate && String(req.sap_delegate) === currentUserId)) {
                        approvalStage = 'Final';
                      }
                      
                      return (
                        <tr
                          key={index}
                          className={`border-b transition cursor-pointer ${
                            isApproverWaitingForInfo(req) 
                              ? "bg-yellow-100 border-l-8 border-orange-500 shadow-md hover:bg-yellow-200 ring-1 ring-orange-200" 
                              : "hover:bg-amber-50"
                          }`}
                          onClick={() => window.location.href = `/dashboard/requisitions/${req.requisition_id}`}
                        >
                          <td className="px-3 py-2 font-bold text-primary hover:underline">
                            {req.requisition_number || `REQ-${req.requisition_id}`}
                          </td>
                          <td className="px-3 py-2">
                            {req.specification_title || 'No project'}
                          </td>
                          <td className="px-3 py-2">
                            <div>{req.user_full_name || 'Unknown'}</div>
                          </td>
                          <td className="px-3 py-2">
                            <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-amber-100 text-amber-700">
                              {approvalStage}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-lg border" style={{borderColor: 'var(--border-light)'}}>
            <div className="px-2 py-1 border-b bg-gradient-to-r from-blue-50 to-white" style={{borderColor: 'var(--border-light)'}}>
              <div className="flex items-baseline justify-between pt-1 pb-0">
                <div className="flex items-baseline gap-2">
                  <div className="w-6 h-6 rounded-md flex items-center justify-center mt-0.5" style={{backgroundColor: '#e3f2fd'}}>
                    <FiClipboard className="w-3.5 h-3.5" style={{color: '#0086D4'}} />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold leading-tight" style={{color: 'var(--text-primary)'}}>
                      My Requisitions
                    </h3>
                    <p className="text-[10px] text-gray-500 leading-tight">{(dashboardData.all_requisitions || []).length} total</p>
                  </div>
                </div>
                <Link
                  to="/dashboard/requisitions"
                  className="text-[11px] font-semibold hover:underline px-2 py-0.5 rounded-md hover:bg-gray-100 transition-colors"
                  style={{color: 'var(--color-primary)'}}>
                  View All →
                </Link>
              </div>
            </div>
            <div className="divide-y" style={{borderColor: 'var(--border-light)'}}>
              {(!dashboardData.all_requisitions || dashboardData.all_requisitions.length === 0) ? (
                <div className="p-8 text-center">
                  <FiClipboard className="w-12 h-12 mx-auto mb-3" style={{color: 'var(--color-primary)', opacity: 0.3}} />
                  <p className="text-sm font-medium" style={{color: 'var(--text-secondary)'}}>No requisitions found</p>
                </div>
              ) : (
                <div className="max-h-[600px] overflow-y-auto">
                  <table className="min-w-full text-xs">
                    <thead>
                      <tr className="bg-gray-50 text-gray-700">
                        <th className="font-semibold px-3 py-2 text-left">Number</th>
                        <th className="font-semibold px-3 py-2 text-left">Project</th>
                        <th className="font-semibold px-3 py-2 text-left">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(dashboardData.all_requisitions || []).slice(0, 10).map((req, index) => {
                        let statusColor = 'bg-yellow-100 text-yellow-700';
                        let statusText = 'Pending';
                        
                        if (req.requisition_status >= req.specification_approvals) {
                          statusColor = 'bg-green-100 text-green-700';
                          statusText = 'Approved';
                        } else if (req.requisition_status > 0) {
                          statusColor = 'bg-blue-100 text-blue-700';
                          statusText = 'In Review';
                        }
                        
                        return (
                          <tr
                            key={index}
                            className={`border-b transition cursor-pointer ${
                              isApproverWaitingForInfo(req) 
                                ? "bg-yellow-100 border-l-8 border-orange-500 shadow-md hover:bg-yellow-200 ring-1 ring-orange-200" 
                                : "hover:bg-blue-50"
                            }`}
                            onClick={() => window.location.href = `/dashboard/requisitions/${req.requisition_id}`}
                          >
                            <td className="px-3 py-2 font-bold text-primary hover:underline">
                              {req.requisition_number || `REQ-${req.requisition_id}`}
                            </td>
                            <td className="px-3 py-2">
                              {req.specification_title || 'No project'}
                            </td>
                            <td className="px-3 py-2">
                              <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${statusColor}`}>
                                {statusText}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Right Column - Recent Support Tickets */}
        <div className="bg-white rounded-xl shadow-lg border" style={{borderColor: 'var(--border-light)'}}>
          <div className="px-2 py-1 border-b bg-gradient-to-r from-blue-50 to-white" style={{borderColor: 'var(--border-light)'}}>
            <div className="flex items-baseline justify-between pt-1 pb-0">
              <div className="flex items-baseline gap-2">
                <div className="w-6 h-6 rounded-md flex items-center justify-center mt-0.5" style={{backgroundColor: '#e3f2fd'}}>
                  <FiAlertTriangle className="w-3.5 h-3.5" style={{color: '#0086D4'}} />
                </div>
                <div>
                  <h3 className="text-sm font-semibold leading-tight" style={{color: 'var(--text-primary)'}}>
                    Recent Support Tickets
                  </h3>
                  <p className="text-[10px] text-gray-500 leading-tight">{(dashboardData.tickets_all || []).length} total</p>
                </div>
              </div>
              <Link
                to="/dashboard/helpdesk"
                className="text-[11px] font-semibold hover:underline px-2 py-0.5 rounded-md hover:bg-gray-100 transition-colors"
                style={{color: 'var(--color-primary)'}}>
                View All →
              </Link>
            </div>
          </div>
          <div className="divide-y" style={{borderColor: 'var(--border-light)'}}>
            {(!dashboardData.tickets_all || dashboardData.tickets_all.length === 0) ? (
              <div className="p-8 text-center">
                <FiAlertTriangle className="w-12 h-12 mx-auto mb-3" style={{color: 'var(--color-info)', opacity: 0.3}} />
                <p className="text-sm font-medium" style={{color: 'var(--text-secondary)'}}>No recent tickets found</p>
              </div>
            ) : (
              <div className="max-h-[600px] overflow-y-auto">
                {(dashboardData.tickets_all || []).slice(0, 10).map((ticket, index) => {
                  let statusColor = 'bg-blue-100 text-blue-700';
                  let statusText = 'Open';
                  
                  if (ticket.status === 'closed') {
                    statusColor = 'bg-green-100 text-green-700';
                    statusText = 'Closed';
                  } else if (ticket.status === 'escalated') {
                    statusColor = 'bg-purple-100 text-purple-700';
                    statusText = 'Escalated';
                  } else if (ticket.status === 'approved') {
                    statusColor = 'bg-green-100 text-green-700';
                    statusText = 'Approved';
                  } else if (ticket.status === 'pending_approval') {
                    statusColor = 'bg-yellow-100 text-yellow-700';
                    statusText = 'Pending Approval';
                  } else if (ticket.status === 'in_progress') {
                    statusColor = 'bg-blue-100 text-blue-700';
                    statusText = 'In Progress';
                  } else if (ticket.assigned_to && ticket.assigned_to > 0) {
                    statusColor = 'bg-blue-100 text-blue-700';
                    statusText = 'Assigned';
                  }
                  
                  return (
                    <div key={index} className="p-3 hover:bg-gradient-to-r hover:from-gray-50 hover:to-white transition-all border-l-4 border-transparent hover:border-blue-400">
                      <Link to={`/dashboard/helpdesk?ticket=${ticket.ticket_id}`} className="block">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-bold text-sm" style={{color: 'var(--text-primary)'}}>
                                #{ticket.ticket_id}
                              </span>
                              <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${statusColor}`}>
                                {statusText}
                              </span>
                            </div>
                            <p className="text-sm font-semibold mb-1 line-clamp-1" style={{color: 'var(--text-primary)'}}>
                              {ticket.title || 'No subject'}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center justify-between text-xs text-gray-500 pt-2 border-t" style={{borderColor: 'var(--border-light)'}}>
                          <span className="truncate">👤 {ticket.user_full_name || 'Unknown'}</span>
                          <span className="ml-2">{ticket.created_at ? new Date(ticket.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'N/A'}</span>
                        </div>
                      </Link>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default StandardDashboard;
